<script lang="ts" setup>
const localePath = useLocalePath()
const switchLocalePath = useSwitchLocalePath()
const { locale: activeLocale, locales } = useI18n()

const menuOpen = ref(false)

// Lock background scrolling while the menu overlay is open
watch(menuOpen, (isOpen) => {
  document.body.style.overflow = isOpen ? 'hidden' : ''
})

// Restore scrolling if the header unmounts while the menu is still open
onUnmounted(() => {
  document.body.style.overflow = ''
})
</script>

<template>
  <header>
    <NuxtLink class="logo" :to="localePath('/')" title="Home">
      <img class="logo" alt="Silvabird logo" src="/img/logos/logo-with-text.svg" />
    </NuxtLink>
    <div class="right-side">
      <button
        class="menu-toggle"
        type="button"
        :aria-expanded="menuOpen"
        aria-label="Menu"
        @click="menuOpen = !menuOpen"
      >
        <SbIcon name="list" />
      </button>
      <nav class="menu" :class="{ open: menuOpen }">
        <NuxtLink class="nav-link" :to="localePath('/support')" @click="menuOpen = false">
          {{ $t('header.support') }}
        </NuxtLink>
        <div class="locale-switch">
          <NuxtLink
            v-for="locale of locales"
            :key="locale.code"
            :to="switchLocalePath(locale.code)"
            :class="{ active: activeLocale === locale.code }"
            @click="menuOpen = false"
          >
            {{ locale.code.toUpperCase() }}
          </NuxtLink>
        </div>
        <AppButton class="login-button" :as-anchor="true" href="https://app.silvabird.com/" @click="menuOpen = false"
          ><SbIcon name="user-circle" /> {{ $t('header.loginButton') }}</AppButton
        >
      </nav>
    </div>
  </header>
  <div v-if="menuOpen" class="menu-backdrop" @click="menuOpen = false" />
</template>

<style lang="scss" scoped>
header {
  height: 40px;
  padding: 20px 40px;

  display: flex;
  justify-content: space-between;
  align-items: center;

  position: sticky;
  top: 0;
  background: var(--background-transparent);
  backdrop-filter: blur(10px);
  z-index: 2;

  .logo {
    height: 100%;
    flex-shrink: 0;
  }

  @media (max-width: 600px) {
    height: 32px;
    padding: 12px 20px;
  }
}

.right-side {
  display: flex;
  align-items: center;
}

.menu {
  display: flex;
  align-items: center;
  gap: 30px;
}

.nav-link {
  color: var(--text);
  text-decoration: none;
  font-weight: bold;
}

.locale-switch {
  display: inline-flex;
  padding: 2px;
  border: 1px solid var(--border-subtle);
  border-radius: 999px;

  a {
    padding: 3px 11px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: bold;
    color: var(--text-subtle);
    text-decoration: none;

    &.active {
      background: var(--background-subtle);
      color: var(--text);
    }

    &:hover:not(.active) {
      color: var(--text);
    }
  }
}

.menu-toggle {
  display: none;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: none;
  border: none;
  color: var(--text);
  cursor: pointer;
}

.menu-backdrop {
  display: none;
}

@media (max-width: 600px) {
  .menu {
    display: none;

    &.open {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 20px;

      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      padding: 24px 20px;

      background: var(--background);
      border-bottom: 1px solid var(--border-subtle);
      box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
      z-index: 3;
    }

    .nav-link {
      font-size: 18px;
    }

    // Keep the pill at its natural size rather than stretching across the panel
    .locale-switch {
      align-self: flex-start;
    }

    .login-button {
      justify-content: center;
    }
  }

  .menu-toggle {
    display: flex;
  }

  .menu-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 1;
  }
}
</style>
